import React, { useState, useEffect } from 'react';

const Testimonials = () => {
  // Using your brand colors
  const colors = {
    primary: "#122721",
    background: "#6cf168",
    accent: "#36893B",
    light: "#f8f9fa",
  };

  // Add fade animation styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fadeIn {
        animation: fadeIn 0.5s ease-in-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const testimonials = [
    {
      rating: 5,
      text: "Arbaah has revolutionized the way we handle our finances. The voice-to-invoice feature alone saves us hours every week. Compliance has never been easier!",
      author: "Mohammed A.",
      company: "Dubai Retail Solutions"
    },
    {
      rating: 5,
      text: "As a small business owner, keeping track of taxes and compliance was always a headache. With Arbaah, I'm alerted about deadlines before they arrive, and creating invoices is faster than ever.",
      author: "Fatima K.",
      company: "Abu Dhabi Consulting"
    },
    {
      rating: 5,
      text: "The document scanning feature is incredibly accurate. I've tried other apps before, but none of them extract data as precisely as Arbaah does. The interface is intuitive and user-friendly too.",
      author: "Ahmed R.",
      company: "Sharjah Import/Export"
    },
    {
      rating: 5,
      text: "Since implementing Arbaah across our company, we've reduced invoice processing time by 80% and eliminated late tax filing penalties. The ROI has been substantial and immediate.",
      author: "Layla M.",
      company: "RAK Enterprises"
    },
    {
      rating: 5,
      text: "I was skeptical about AI-powered financial tools, but Arbaah has exceeded my expectations. The voice recognition is spot-on, even with my accent, and the tax compliance features are a lifesaver.",
      author: "Saeed J.",
      company: "Ajman Tech Solutions"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const maxVisibleDesktop = 3;

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        nextSlide();
      }, 5000); // 5 seconds

      return () => clearInterval(interval);
    }
  }, [currentIndex, isPaused]);

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <svg 
        key={i} 
        className="w-4 h-4 md:w-5 md:h-5 inline-block" 
        viewBox="0 0 20 20" 
        fill={i < rating ? colors.accent : "none"} 
        stroke={colors.accent}
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <section id="testimonials" className="py-12 md:py-16 relative overflow-hidden" style={{ backgroundColor: colors.light }}>
      {/* Background with abstract shape gradient */}
      <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
        <svg viewBox="0 0 1440 420" className="absolute top-0 left-0 w-full h-auto opacity-20">
          <path fill={colors.background} fillOpacity="0.4" d="M0,224L48,208C96,192,192,160,288,170.7C384,181,480,235,576,245.3C672,256,768,224,864,186.7C960,149,1056,107,1152,96C1248,85,1344,107,1392,117.3L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
        <div className="absolute bottom-0 right-0 w-1/3 h-2/3 bg-gradient-to-bl from-[#36893B] to-transparent opacity-30 rounded-full blur-3xl transform translate-x-1/4"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4" style={{ color: colors.primary }}>
            What Our Customers Say
          </h2>
          <p className="text-base md:text-lg mx-auto max-w-3xl px-4" style={{ color: colors.primary }}>
            Trusted by Leading UAE Businesses
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto overflow-hidden"
             onMouseEnter={() => setIsPaused(true)}
             onMouseLeave={() => setIsPaused(false)}>
          {/* Mobile view (1 testimonial) */}
          <div className="md:hidden">
            <div className="flex justify-center px-4">
              <div 
                className="bg-accent text-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 w-full max-w-md animate-fadeIn"
                style={{ backgroundColor: colors.primary }}
                key={currentIndex} // Add key to trigger re-render for animation
              >
                <div className="p-5 md:p-6">
                  <div className="mb-3 md:mb-4">
                    {Array(5).fill(0).map((_, i) => (
                      <svg 
                        key={i} 
                        className="w-4 h-4 md:w-5 md:h-5 inline-block mr-1" 
                        viewBox="0 0 20 20" 
                        fill={i < testimonials[currentIndex].rating ? "white" : "none"} 
                        stroke="white"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-sm md:text-lg mb-4 md:mb-6 text-white">
                    "{testimonials[currentIndex].text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-accent font-bold text-lg md:text-xl"
                         style={{ color: colors.primary }}>
                      {testimonials[currentIndex].author.charAt(0)}
                    </div>
                    <div className="ml-3 md:ml-4">
                      <p className="font-semibold text-white text-sm md:text-base">
                        {testimonials[currentIndex].author}
                      </p>
                      <p className="text-xs md:text-sm text-white text-opacity-80">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tablet view (2 testimonials) */}
          <div className="hidden md:block lg:hidden">
            <div className="flex space-x-4 md:space-x-6">
              {/* Main testimonial (inverted colors) */}
              <div 
                className="bg-accent text-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 w-3/5 animate-fadeIn"
                style={{ backgroundColor: colors.primary }}
                key={`tablet-${currentIndex}`}
              >
                <div className="p-5 md:p-6">
                  <div className="mb-3 md:mb-4">
                    {renderStars(testimonials[currentIndex].rating)}
                  </div>
                  
                  <p className="text-base md:text-lg mb-4 md:mb-6 text-white">
                    "{testimonials[currentIndex].text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-accent font-bold text-lg md:text-xl"
                         style={{ color: colors.primary }}>
                      {testimonials[currentIndex].author.charAt(0)}
                    </div>
                    <div className="ml-3 md:ml-4">
                      <p className="font-semibold text-white text-sm md:text-base">
                        {testimonials[currentIndex].author}
                      </p>
                      <p className="text-xs md:text-sm text-white text-opacity-80">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Secondary testimonial (white background) */}
              <div 
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 w-2/5"
              >
                <div className="p-5 md:p-6">
                  <div className="mb-3 md:mb-4">
                    {renderStars(testimonials[(currentIndex + 1) % testimonials.length].rating)}
                  </div>
                  
                  <p className="text-sm md:text-base mb-4 md:mb-6" style={{ color: colors.primary }}>
                    "{testimonials[(currentIndex + 1) % testimonials.length].text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-[#6cf168] to-[#36893B] rounded-full flex items-center justify-center text-white font-bold">
                      {testimonials[(currentIndex + 1) % testimonials.length].author.charAt(0)}
                    </div>
                    <div className="ml-2 md:ml-3">
                      <p className="font-semibold text-xs md:text-sm" style={{ color: colors.primary }}>
                        {testimonials[(currentIndex + 1) % testimonials.length].author}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-600">
                        {testimonials[(currentIndex + 1) % testimonials.length].company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop view (3 testimonials with 4th cut off) */}
          <div className="hidden lg:block">
            <div className="flex space-x-6 relative overflow-hidden">
              {/* Main testimonial (inverted colors) */}
              <div 
                className="bg-accent text-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 w-2/5 animate-fadeIn"
                style={{ backgroundColor: colors.primary }}
                key={`desktop-${currentIndex}`}
              >
                <div className="p-6">
                  <div className="mb-4">
                    {renderStars(testimonials[currentIndex].rating)}
                  </div>
                  
                  <p className="text-lg mb-6 text-white">
                    "{testimonials[currentIndex].text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-accent font-bold text-xl"
                         style={{ color: colors.accent }}>
                      {testimonials[currentIndex].author.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <p className="font-semibold text-white">
                        {testimonials[currentIndex].author}
                      </p>
                      <p className="text-sm text-white text-opacity-80">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Secondary testimonials (white background) */}
              {[1, 2].map((offset) => (
                <div 
                  key={offset}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500"
                  style={{ 
                    width: offset === 1 ? '30%' : '25%',
                    opacity: offset === 1 ? 1 : 0.9
                  }}
                >
                  <div className="p-5">
                    <div className="mb-3">
                      {renderStars(testimonials[(currentIndex + offset) % testimonials.length].rating)}
                    </div>
                    
                    <p className={`${offset === 1 ? 'text-base' : 'text-sm'} mb-5`} style={{ color: colors.primary }}>
                      "{testimonials[(currentIndex + offset) % testimonials.length].text}"
                    </p>
                    
                    <div className="flex items-center">
                      <div className={`${offset === 1 ? 'w-10 h-10' : 'w-8 h-8'} bg-gradient-to-br from-[#6cf168] to-[#36893B] rounded-full flex items-center justify-center text-white font-bold`}>
                        {testimonials[(currentIndex + offset) % testimonials.length].author.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <p className={`font-semibold ${offset === 1 ? 'text-sm' : 'text-xs'}`} style={{ color: colors.primary }}>
                          {testimonials[(currentIndex + offset) % testimonials.length].author}
                        </p>
                        <p className={`${offset === 1 ? 'text-xs' : 'text-xs'} text-gray-600`}>
                          {testimonials[(currentIndex + offset) % testimonials.length].company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Navigation controls */}
          <div className="flex justify-center mt-8 md:mt-10 gap-4 md:gap-6">
            <button
              onClick={() => {
                prevSlide();
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 5000); // Resume after 5 seconds
              }}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent flex items-center justify-center hover:opacity-90 transition-all duration-300"
              style={{ backgroundColor: colors.primary }}
              aria-label="Previous testimonial"
            >
              <svg 
                className="w-5 h-5 md:w-6 md:h-6" 
                viewBox="0 0 24 24" 
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M15 18L9 12L15 6" 
                  stroke="white" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            
            <button
              onClick={() => {
                nextSlide();
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 5000); // Resume after 5 seconds
              }}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent flex items-center justify-center hover:opacity-90 transition-all duration-300"
              style={{ backgroundColor: colors.primary }}
              aria-label="Next testimonial"
            >
              <svg 
                className="w-5 h-5 md:w-6 md:h-6" 
                viewBox="0 0 24 24" 
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M9 6L15 12L9 18" 
                  stroke="white" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          
          {/* Pagination dots */}
          <div className="flex justify-center mt-4 gap-1.5">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 5000);
                }}
                className={`transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 h-2 rounded-full' 
                    : 'w-2 h-2 rounded-full hover:opacity-70'
                }`}
                style={{ 
                  backgroundColor: index === currentIndex ? colors.primary : '#D1D5DB'
                }}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;